/**
 * Main layout component with AppBar, navigation, and user menu
 * Includes logout functionality and language switcher
 */
import React, { useState, Suspense, useMemo, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
    AppBar,
    Box,
    Toolbar,
    Typography,
    IconButton,
    Menu,
    MenuItem,
    Container,
    Avatar,
    Tooltip,
    ListItemIcon,
    Divider,
    Chip,
    Tabs,
    Tab,
    Drawer,
    List,
    ListItemButton,
    ListItemText,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    AccountCircle,
    Logout as LogoutIcon,
    Menu as MenuIcon,
} from '@mui/icons-material';
import { useAuthStore } from '@store/auth.store';
import { useAuth } from '@features/auth/hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { APP_NAME } from '@core/constants/app.constants';
import { ROUTES } from '@core/constants/routes.constants';
import { MAIN_NAV_ITEMS } from '@core/config/mainNavigation.config';
import { LoadingFallback } from '@components/common/LoadingFallback/LoadingFallback';
import { formatRoleForDisplay } from '@features/users/utils/role.utils';
import logo from '../../../assets/who_logo.svg';

/**
 * Main layout component
 * @returns JSX Element
 */
const MainLayout: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuthStore();
    const { logout } = useAuth();
    const { t } = useTranslation('common');
    const isBelowMd = useMediaQuery(theme.breakpoints.down('md'));

    const isUserAdmin = user?.roles?.some((role) => role.toLowerCase() === 'admin') || false;

    const isActiveRoute = (route: string): boolean => {
        if (route === ROUTES.TEMPLATES) {
            return location.pathname === ROUTES.TEMPLATES || location.pathname.startsWith(ROUTES.TEMPLATES + '/');
        }
        if (route === ROUTES.SYSTEM_PROMPT) {
            return location.pathname === ROUTES.SYSTEM_PROMPT;
        }
        if (route === ROUTES.REFERENCE_LIST_CONFIGURATION) {
            return (
                location.pathname === ROUTES.REFERENCE_LIST_CONFIGURATION ||
                location.pathname.startsWith(`${ROUTES.REFERENCE_LIST_CONFIGURATION}/`)
            );
        }
        if (route === ROUTES.ZONES_CONFIGURATION) {
            return (
                location.pathname === ROUTES.ZONES_CONFIGURATION ||
                location.pathname.startsWith(`${ROUTES.ZONES_CONFIGURATION}/`)
            );
        }
        return location.pathname === route || location.pathname.startsWith(route + '/');
    };

    const visibleNavItems = useMemo(
        () => MAIN_NAV_ITEMS.filter((item) => !item.requiresAdmin || isUserAdmin),
        [isUserAdmin],
    );

    const selectedNavRoute =
        visibleNavItems.find((item) => isActiveRoute(item.route))?.route ?? false;

    const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);

    useEffect(() => {
        setMobileNavOpen(false);
    }, [location.pathname]);

    const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>): void => {
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseUserMenu = (): void => {
        setAnchorElUser(null);
    };

    const handleLogout = (): void => {
        handleCloseUserMenu();
        logout();
    };

    const handleDesktopNavChange = (_: React.SyntheticEvent, route: string): void => {
        navigate(route);
    };

    const navTabsSx = useMemo(
        () => ({
            minHeight: 48,
            flex: 1,
            minWidth: 0,
            '& .MuiTabs-flexContainer': { gap: 0 },
            '& .MuiTab-root': {
                minWidth: 'auto',
                px: 1.25,
                py: 1,
                minHeight: 48,
                color: alpha(theme.palette.primary.contrastText, 0.72),
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 400,
            },
            '& .Mui-selected': {
                color: `${theme.palette.primary.contrastText} !important`,
                fontWeight: 600,
            },
            '& .MuiTabs-indicator': {
                backgroundColor: theme.palette.primary.contrastText,
                height: 2,
            },
            '& .MuiTabs-scrollButtons': {
                color: theme.palette.primary.contrastText,
            },
        }),
        [theme],
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
            <AppBar position="sticky">
                <Container maxWidth={false} sx={{ maxWidth: '100%' }}>
                    <Toolbar disableGutters sx={{ px: 2, gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, mr: { xs: 1, md: 2 } }}>
                            <Box
                                component="img"
                                src={logo}
                                alt="WHO Logo"
                                sx={{
                                    height: 40,
                                    width: 100,
                                    mr: 2,
                                }}
                            />
                            <Typography
                                variant="h6"
                                noWrap
                                component="div"
                                sx={{
                                    color: 'primary.contrastText',
                                    fontWeight: 500,
                                    fontSize: { xs: '14px', sm: '16px' },
                                    cursor: 'pointer',
                                    maxWidth: { xs: 160, sm: 280, md: 'none' },
                                }}
                                onClick={() => navigate(ROUTES.TEMPLATES)}
                            >
                                {APP_NAME}
                            </Typography>
                        </Box>

                        {!isBelowMd && (
                            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center', mx: 1 }}>
                                <Tabs
                                    value={selectedNavRoute}
                                    onChange={handleDesktopNavChange}
                                    variant="scrollable"
                                    scrollButtons="auto"
                                    allowScrollButtonsMobile
                                    aria-label={t('navigation.mainNavigation')}
                                    sx={navTabsSx}
                                >
                                    {visibleNavItems.map((item) => (
                                        <Tab
                                            key={item.route}
                                            label={t(item.labelKey)}
                                            value={item.route}
                                            disableRipple
                                            onClick={() => navigate(item.route)}
                                        />
                                    ))}
                                </Tabs>
                            </Box>
                        )}

                        {isBelowMd && <Box sx={{ flex: 1, minWidth: 0 }} />}

                        {isBelowMd && (
                            <Tooltip title={t('navigation.openMainMenu')}>
                                <IconButton
                                    color="inherit"
                                    edge="end"
                                    onClick={() => setMobileNavOpen(true)}
                                    aria-label={t('navigation.openMainMenu')}
                                    sx={{ color: 'primary.contrastText' }}
                                >
                                    <MenuIcon />
                                </IconButton>
                            </Tooltip>
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                            <Tooltip
                                title={
                                    user?.first_name && user?.last_name
                                        ? `${user.first_name} ${user.last_name}`
                                        : user?.name || 'User'
                                }
                            >
                                <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                                    <Avatar sx={{ bgcolor: 'grey.500', width: 32, height: 32 }}>
                                        {user?.first_name && user?.last_name
                                            ? `${user.first_name.charAt(0).toUpperCase()}${user.last_name.charAt(0).toUpperCase()}`
                                            : user?.name?.charAt(0).toUpperCase() || 'U'}
                                    </Avatar>
                                </IconButton>
                            </Tooltip>
                            <Menu
                                sx={{ mt: '45px' }}
                                id="menu-appbar"
                                anchorEl={anchorElUser}
                                anchorOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                                keepMounted
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                                open={Boolean(anchorElUser)}
                                onClose={handleCloseUserMenu}
                            >
                                <MenuItem sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 1.5 }}>
                                    <Typography
                                        textAlign="left"
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            mb: 1,
                                            maxWidth: '200px',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                        title={
                                            user?.first_name && user?.last_name
                                                ? `${user.first_name} ${user.last_name}`
                                                : user?.name || user?.email || 'User'
                                        }
                                    >
                                        {(() => {
                                            const fullName =
                                                user?.first_name && user?.last_name
                                                    ? `${user.first_name} ${user.last_name}`
                                                    : user?.name || user?.email || 'User';
                                            return fullName.length > 50 ? `${fullName.substring(0, 50)}...` : fullName;
                                        })()}
                                    </Typography>
                                    {user?.roles && user.roles.length > 0 && (
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                            {user.roles.map((role) => (
                                                <Chip
                                                    key={role}
                                                    label={formatRoleForDisplay(role)}
                                                    size="small"
                                                    color="primary"
                                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                                />
                                            ))}
                                        </Box>
                                    )}
                                </MenuItem>
                                <Divider />
                                <MenuItem
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleCloseUserMenu();
                                        navigate(ROUTES.PROFILE);
                                    }}
                                >
                                    <ListItemIcon>
                                        <AccountCircle fontSize="small" />
                                    </ListItemIcon>
                                    {t('navigation.profile')}
                                </MenuItem>
                                <Divider />
                                <MenuItem
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleLogout();
                                    }}
                                >
                                    <ListItemIcon>
                                        <LogoutIcon fontSize="small" />
                                    </ListItemIcon>
                                    {t('navigation.logout')}
                                </MenuItem>
                            </Menu>
                        </Box>
                    </Toolbar>
                </Container>
            </AppBar>

            <Drawer
                anchor="left"
                open={isBelowMd && mobileNavOpen}
                onClose={() => setMobileNavOpen(false)}
                ModalProps={{ keepMounted: true }}
                PaperProps={{
                    sx: { width: 280, pt: 1 },
                }}
            >
                <Box component="nav" aria-label={t('navigation.mainNavigation')}>
                    <List disablePadding>
                        {visibleNavItems.map((item) => (
                            <ListItemButton
                                key={item.route}
                                selected={isActiveRoute(item.route)}
                                onClick={() => navigate(item.route)}
                                sx={{
                                    py: 1.25,
                                    '&.Mui-selected': {
                                        bgcolor: 'action.selected',
                                        borderLeft: (th) => `4px solid ${th.palette.primary.main}`,
                                    },
                                }}
                            >
                                <ListItemText primary={t(item.labelKey)} primaryTypographyProps={{ variant: 'body2' }} />
                            </ListItemButton>
                        ))}
                    </List>
                </Box>
            </Drawer>

            <Box
                component="main"
                sx={{
                    height: 'calc(100vh - 64px)',
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: 'background.default',
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                        }}
                    >
                        <Suspense fallback={<LoadingFallback fullScreen={false} />}>
                            <Outlet />
                        </Suspense>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default MainLayout;
